import React, { useMemo } from 'react';
import { formatNumber } from 'utils/numberFormat';
import { liquid } from 'utils/liquid';
import { format as formatDate } from 'date-fns';
import * as locales from 'date-fns/locale';
import sanitizeHtml from 'sanitize-html';

const getFilters = (user, numberFormat, language) => {
  // use the user's number format and language, unless overridden by the explicit parameters
  numberFormat = numberFormat || user.numberFormat;
  language = language || user.language;

  // wraps the specified filter function, passing it as the last argument all (any) named arguments as an object
  const filter = f => (value, ...args) => {
    const positionalArguments = args.filter(arg => !Array.isArray(arg));
    const namedArguments = Object.fromEntries(args.filter(arg => Array.isArray(arg)));
    return f(value, ...positionalArguments, namedArguments);
  };

  /**
   * Formats the specified number based on the user's number format, taking into account any `locale` or `options`
   * overrides specified as arguments.
   *
   * The formatting is implemented using an `Intl.NumberFormat` object. The `locale` and `options` arguments, if
   * specified, override the corresponding options of the `Intl.NumberFormat` object, cf.:
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
   */
  const format_number = filter((number, { locale, ...options }) => formatNumber(number, numberFormat, locale, options));

  /**
   * Formats the specified number using a compact notation (with suffixes `K`, `M`, etc.), based on the user's number
   * format and taking into account any `locale` or `options` overrides specified as arguments.
   *
   * This is a shortcut for calling `format_number` and specifying `locale` and `option` overrides as follows:
   * `{{ ... | format_number: locale: 'en-US', notation: 'compact' }}`.
   */
  const format_number_compact = filter((number, { locale, ...options }) => {
    // use the 'en-US' locale by default to get a compact notation using 'K', 'M', etc.:
    //   - here are some examples of the compact notations for different locales:
    //                           en-US   de-DE      fr-FR    it-IT     pt-BR
    //           1234.567   ->   1.2K    1235       1,2 k    1235      1,2 mil
    //        1234567.891   ->   1.2M    1.2 Mio.   1,2 M    1,2 Mio   1,2 mi
    //     1234567891.234   ->   1.2B    1.2 Mrd.   1,2 Md   1,2 Mrd   1,2 bi
    //   - note that e.g. German and Italian don't label thousands for some reason
    //   - note that the examples use the default formatting options: however, we pass the user's number format to
    //     `formatNumber`, which changes the resulting formatting
    const localeOverride = locale || 'en-US';
    const optionsOverrides = { notation: 'compact', ...options };
    return formatNumber(number, numberFormat, localeOverride, optionsOverrides);
  });

  /**
   * Formats the specified date using the `format` function from the `date-fns` library, based on the specified format
   * and options.
   *
   * @see https://date-fns.org/v2.29.3/docs/format
   */
  const format_date = filter((date, format, options) => {
    // we use the user language (i.e. current KG language) and not the locale of the user's number format, since the
    // formatted date might contain prose text such as month names, week days etc.
    const locale = options?.locale || language;
    options = {
      ...options,
      // set the appropriate 'date-fns' locale, falling back to 'en-US' if not found
      locale: locales[locale.replace('-', '')] || locales.enUS
    };
    return formatDate(new Date(date), format, options);
  });

  return {
    format_number: format_number,
    format_number_compact: format_number_compact,
    format_date: format_date
  };
};

const getFormattedValue = (value, user, numberFormat) => {
  // use the user's number format, unless overridden by the explicit parameter
  numberFormat = numberFormat || user.numberFormat;
  return typeof value === 'number' ? formatNumber(value, numberFormat) : value;
};

const getScope = (value, formattedValue, additionalScope) => ({
  value: value,
  formatted_value: formattedValue,
  ...additionalScope
});

/**
 * Renders a value synchronously using the specified HTML template, providing it with a useful scope and filters.
 */
export const liquidFormattedValue = (
  value,
  additionalScope,
  user,
  htmlTemplate,
  numberFormat,
  language,
  skipHtmlSanitization
) => {
  const formattedValue = getFormattedValue(value, user, numberFormat);
  if (htmlTemplate) {
    const renderedTemplate = liquid(
      htmlTemplate,
      getScope(value, formattedValue, additionalScope),
      getFilters(user, numberFormat, language)
    );
    return skipHtmlSanitization ? renderedTemplate : sanitizeHtml(renderedTemplate, getHtmlSanitizationOptions());
  }
  return formattedValue;
};

/**
 * Renders a value using the specified HTML template, providing it with a useful scope and filters.
 */
const LiquidFormattedValue = ({
  value,
  additionalScope,
  user,
  htmlTemplate,
  numberFormat,
  language,
  as: Component = 'div'
}) => {
  const renderedValue = useMemo(
    () => liquidFormattedValue(value, additionalScope, user, htmlTemplate, numberFormat, language),
    [value, additionalScope, user, htmlTemplate, numberFormat, language]
  );
  return <Component dangerouslySetInnerHTML={{ __html: renderedValue }} />;
};

export default LiquidFormattedValue;

window.veezoo = {
  ...window.veezoo,
  /**
   * Exposes the `liquidFormattedValue` function such that it can be used in the Highcharts 'format' templating helper.
   */
  liquidFormattedValue: (value, htmlTemplate, numberFormat, language) =>
    liquidFormattedValue(value, null, null, htmlTemplate, numberFormat, language)
};

/**
 * Returns the HTML sanitization options used by the `LiquidFormattedValue` component.
 *
 * The rules have been copied from Looker, cf. https://cloud.google.com/looker/docs/html-sanitization.
 */
const getHtmlSanitizationOptions = () => ({
  allowedTags: [
    'a',
    'abbr',
    'acronym',
    'address',
    'area',
    'article',
    'aside',
    'audio',
    'b',
    'bdi',
    'bdo',
    'big',
    'blockquote',
    'br',
    'button',
    'canvas',
    'caption',
    'center',
    'cite',
    'code',
    'col',
    'colgroup',
    'datalist',
    'dd',
    'del',
    'details',
    'dfn',
    'dir',
    'div',
    'dl',
    'dt',
    'em',
    'fieldset',
    'figcaption',
    'footer',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'header',
    'ins',
    'hr',
    'i',
    'img',
    'kbd',
    'label',
    'legend',
    'li',
    'map',
    'mark',
    'menu',
    'meter',
    'nav',
    'ol',
    'output',
    'p',
    'pre',
    'q',
    's',
    'samp',
    'section',
    'small',
    'source',
    'span',
    'strike',
    'strong',
    'sub',
    'summary',
    'sup',
    'table',
    'tbody',
    'td',
    'tfoot',
    'th',
    'thead',
    'time',
    'tr',
    'track',
    'tt',
    'u',
    'ul',
    'var',
    'video'
  ],
  allowedAttributes: {
    '*': [
      'abbr',
      'accept',
      'accept-charset',
      'accesskey',
      'action',
      'align',
      'alt',
      'autoplay',
      'axis',
      'border',
      'cellpadding',
      'cellspacing',
      'char',
      'charoff',
      'charset',
      'checked',
      'cite',
      'class',
      'clear',
      'color',
      'cols',
      'colspan',
      'compact',
      'controls',
      'controlslist',
      'coords',
      'datetime',
      'dir',
      'disabled',
      'enctype',
      'for',
      'frame',
      'headers',
      'height',
      'href',
      'hreflang',
      'hspace',
      'id',
      'ismap',
      'label',
      'lang',
      'longdesc',
      'loop',
      'loopcount',
      'loopend',
      'loopstart',
      'maxlength',
      'media',
      'method',
      'multiple',
      'muted',
      'name',
      'nohref',
      'noshade',
      'nowrap',
      'poster',
      'preload',
      'prompt',
      'readonly',
      'rel',
      'rev',
      'rows',
      'rowspan',
      'rules',
      'scope',
      'selected',
      'shape',
      'size',
      'span',
      'src',
      'start',
      'style',
      'summary',
      'tabindex',
      'target',
      'title',
      'type',
      'usemap',
      'valign',
      'value',
      'vspace',
      'width',
      'xml:lang'
    ]
  },
  allowedStyles: {
    '*': Object.fromEntries(
      [
        '-moz-border-radius',
        '-moz-border-radius-bottomleft',
        '-moz-border-radius-bottomright',
        '-moz-border-radius-topleft',
        '-moz-border-radius-topright',
        '-moz-box-shadow',
        '-moz-outline',
        '-moz-outline-color',
        '-moz-outline-style',
        '-moz-outline-width',
        '-o-text-overflow',
        '-webkit-border-bottom-left-radius',
        '-webkit-border-bottom-right-radius',
        '-webkit-border-radius',
        '-webkit-border-radius-bottom-left',
        '-webkit-border-radius-bottom-right',
        '-webkit-border-radius-top-left',
        '-webkit-border-radius-top-right',
        '-webkit-border-top-left-radius',
        '-webkit-border-top-right-radius',
        '-webkit-box-shadow',
        'azimuth',
        'background',
        'background-attachment',
        'background-color',
        'background-image',
        'background-position',
        'background-repeat',
        'border',
        'border-bottom',
        'border-bottom-color',
        'border-bottom-left-radius',
        'border-bottom-right-radius',
        'border-bottom-style',
        'border-bottom-width',
        'border-collapse',
        'border-color',
        'border-left',
        'border-left-color',
        'border-left-style',
        'border-left-width',
        'border-radius',
        'border-right',
        'border-right-color',
        'border-right-style',
        'border-right-width',
        'border-spacing',
        'border-style',
        'border-top',
        'border-top-color',
        'border-top-left-radius',
        'border-top-right-radius',
        'border-top-style',
        'border-top-width',
        'border-width',
        'box-shadow',
        'caption-side',
        'clear',
        'color',
        'cue',
        'cue-after',
        'cue-before',
        'cursor',
        'direction',
        'display',
        'elevation',
        'empty-cells',
        'float',
        'font',
        'font-family',
        'font-size',
        'font-stretch',
        'font-style',
        'font-variant',
        'font-weight',
        'height',
        'image()',
        'letter-spacing',
        'line-height',
        'linear-gradient()',
        'list-style',
        'list-style-image',
        'list-style-position',
        'list-style-type',
        'margin',
        'margin-bottom',
        'margin-left',
        'margin-right',
        'margin-top',
        'max-height',
        'max-width',
        'min-height',
        'min-width',
        'outline',
        'outline-color',
        'outline-style',
        'outline-width',
        'overflow',
        'padding',
        'padding-bottom',
        'padding-left',
        'padding-right',
        'padding-top',
        'pause',
        'pause-after',
        'pause-before',
        'pitch',
        'pitch-range',
        'quotes',
        'radial-gradient()',
        'rect()',
        'repeating-linear-gradient()',
        'repeating-radial-gradient()',
        'rgb()',
        'rgba()',
        'richness',
        'speak',
        'speak-header',
        'speak-numeral',
        'speak-punctuation',
        'speech-rate',
        'stress',
        'table-layout',
        'text-align',
        'text-decoration',
        'text-indent',
        'text-overflow',
        'text-shadow',
        'text-transform',
        'text-wrap',
        'unicode-bidi',
        'vertical-align',
        'voice-family',
        'volume',
        'white-space',
        'width',
        'word-spacing',
        'word-wrap'
      ]
        // allow any value for all styles
        .map(name => [name, [/^.*$/]])
    )
  },
  transformTags: {
    a: (tagName, attribs) => ({
      tagName: tagName,
      attribs: {
        ...attribs,
        // force links to open in a new window
        target: '_blank'
      }
    })
  }
});
